import React, { useMemo, useState } from 'react';
import AddOutlined from '@mui/icons-material/AddOutlined';
import { Box, Stack, Typography } from '@mui/material';
import { Button } from '../../../../../components/ui';
import { useFeatureTheme } from '../../../../../theme/hooks/useFeatureTheme';
import { FORM_STUDIO_COPY } from '../../constants/formStudioConfig';
import { hydrateTriggerForEdit } from '../../utils/triggerFormUtils';
import TriggerEditorDrawer from './TriggerEditorDrawer';
import TriggerSummaryCard from './TriggerSummaryCard';

export default function StatusLevelTriggerBlock({
  statusRow,
  statusOptionName,
  workspaceId,
  disabled = false,
  isSaving = false,
  onSaveTriggers,
}) {
  const { semantic, typographyStyles } = useFeatureTheme();
  const copy = FORM_STUDIO_COPY.automations.statusTriggers;
  const triggers = Array.isArray(statusRow?.triggers) ? statusRow.triggers : [];

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);

  const contextLabel = `${statusOptionName} · ${copy.statusLevelLabel}`;

  const drawerTrigger = useMemo(() => {
    if (!drawerOpen) return null;
    if (editingIndex == null) return null;
    if (editingIndex === 'create') return null;
    return hydrateTriggerForEdit(triggers[editingIndex]);
  }, [drawerOpen, editingIndex, triggers]);

  const closeDrawer = () => {
    setDrawerOpen(false);
    setEditingIndex(null);
  };

  const openCreate = () => {
    setEditingIndex('create');
    setDrawerOpen(true);
  };

  const openEdit = (index) => {
    setEditingIndex(index);
    setDrawerOpen(true);
  };

  const handleSave = async (draft, index) => {
    const next = [...triggers];
    if (index == null || index === 'create') {
      next.push(draft);
    } else {
      next[index] = draft;
    }
    const result = await onSaveTriggers?.(next);
    if (result?.success !== false) {
      closeDrawer();
    }
  };

  const handleDelete = async (index) => {
    const next = triggers.filter((_, itemIndex) => itemIndex !== index);
    await onSaveTriggers?.(next);
  };

  return (
    <Box>
      <Typography sx={{ ...typographyStyles.bodyText, fontWeight: 600, mb: 1 }}>
        {copy.statusTriggersTitle}
      </Typography>
      <Stack spacing={1}>
        {triggers.map((trigger, index) => (
          <TriggerSummaryCard
            key={`status-trigger-${index}`}
            trigger={trigger}
            disabled={disabled || isSaving}
            onEdit={() => openEdit(index)}
            onDelete={() => handleDelete(index)}
          />
        ))}
        {triggers.length === 0 ? (
          <Typography sx={{ ...typographyStyles.caption, color: semantic.text.secondary }}>
            {copy.noTriggers}
          </Typography>
        ) : null}
        <Button
          variant="outline"
          icon={<AddOutlined />}
          disabled={disabled || isSaving}
          onClick={openCreate}
        >
          {copy.addTrigger}
        </Button>
      </Stack>

      <TriggerEditorDrawer
        open={drawerOpen}
        contextLabel={contextLabel}
        trigger={drawerTrigger}
        triggerIndex={editingIndex === 'create' ? null : editingIndex}
        workspaceId={workspaceId}
        isSaving={isSaving}
        onClose={closeDrawer}
        onSave={handleSave}
      />
    </Box>
  );
}
